import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import type { Params, Path } from "@/router";

import { LinkProps } from "@generouted/react-router/client";
import { MenuItem } from "@/components/Menu";
import Button from "@/components/Button";
import { useQuery } from "@apollo/client";
import { GET_PDR_HISTORY_DETAILS_QUERY } from "./queries";
import { useParams } from "react-router-dom";
import { PDRHistoryDetailsData } from "./types";
import { EditParticipationModal, DeleteParticipationModal } from "@/features/participation";
import { formatPDRHistoryDetailsForForm } from "../_utils";
import ErrorBannerProvider from "@/providers/ErrorBannerProvider";
import { useHasCapabilityBool } from "@/hooks/useCapabilities";
import { DOMAIN, PERMISSION } from "@/providers/AuthorizationProvider";
import { getParticipationActionPermissions } from "@/pages/activities/activity-types/[activityTypeId]/participationUtils";
import { isCcmsLegacyPdrCredit, isTypeLevelPdrParticipation } from "@/features/participation";

export default function ActivityIdLayout<P extends Path>() {
  const { pgaId = "", activityId = "" } = useParams();
  const [actions, setActions] = useState<React.ReactNode>(null);
  const [extendedActions, setExtendedActions] = useState<MenuItem[]>([]);
  const [editParticipantModalOpen, setEditParticipantModalOpen] = useState(false);
  const [deleteParticipantModalOpen, setDeleteParticipantModalOpen] = useState(false);
  const canManageParticipations = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageParticipations
  });
  const canManageLegacyPdrCredits = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageLegacyPdrCredits
  });
  const [backLink, setBackLink] = useState<LinkProps<P, Params>>({ to: "/", title: "Back" } as unknown as LinkProps<
    P,
    Params
  >);

  const { data, loading, error } = useQuery<PDRHistoryDetailsData>(GET_PDR_HISTORY_DETAILS_QUERY, {
    variables: { id: activityId, pgaId }
  });
  const activityParticipation = data?.pdrHistoryDetails?.activityParticipation;
  const isTypeLevelParticipation = isTypeLevelPdrParticipation({
    activityParticipation: data?.pdrHistoryDetails?.activityParticipation
  });
  const isCcmsLegacyCredit = isCcmsLegacyPdrCredit({ activityParticipation });
  const { canShowEdit, canShowDelete } = getParticipationActionPermissions({
    canManageParticipations,
    isTypeLevelParticipation,
    isCcmsLegacyCredit,
    canManageLegacyPdrCredits,
    hasCredit: !!data?.pdrHistoryDetails?.id,
    canEdit: activityParticipation?.canEdit,
    canDelete: activityParticipation?.canDelete
  });

  useEffect(() => {
    setBackLink({ to: `/people/${pgaId}/education/professional-development` } as LinkProps<P, Params>);
  }, [pgaId]);

  useEffect(() => {
    const compiledActions = [];

    if (data) {
      if (canShowDelete) {
        compiledActions.push(
          <Button
            key="delete-participation"
            icon="delete"
            variant="text"
            onClick={() => setDeleteParticipantModalOpen(true)}
          >
            Delete Participation
          </Button>
        );
      }
      if (canShowEdit) {
        compiledActions.push(
          <Button key="edit-participation" variant="filled" onClick={() => setEditParticipantModalOpen(true)}>
            Edit Participation
          </Button>
        );
      }
    }

    setActions(compiledActions);
  }, [data, canShowDelete, canShowEdit]);

  const contextValue = {
    data,
    loading,
    error,
    setActions,
    setBackLink,
    setExtendedActions
  };

  const formattedPDRHistoryDetails = formatPDRHistoryDetailsForForm(data, pgaId);

  return (
    <ErrorBannerProvider>
      <FocusedPageLayout backLink={backLink} actions={actions} extendedActions={extendedActions}>
        <Outlet context={contextValue} />
        {editParticipantModalOpen && (
          <EditParticipationModal
            onDismiss={() => setEditParticipantModalOpen(false)}
            pgaId={pgaId || ""}
            activityId={activityId}
            activityParticipationId={data?.pdrHistoryDetails?.activityParticipation?.id}
            isTypeLevelParticipation={isTypeLevelParticipation}
            defaultValues={isTypeLevelParticipation ? undefined : formattedPDRHistoryDetails}
            skipPdrQuery={!isTypeLevelParticipation && !!activityParticipation?.id}
            additionalRefetchQueries={
              isTypeLevelParticipation
                ? [{ query: GET_PDR_HISTORY_DETAILS_QUERY, variables: { id: activityId, pgaId } }]
                : undefined
            }
          />
        )}
        {deleteParticipantModalOpen && (
          <DeleteParticipationModal
            onDismiss={() => setDeleteParticipantModalOpen(false)}
            activityId={activityId}
            activityParticipationId={activityParticipation?.id}
            isTypeLevelParticipation={isTypeLevelParticipation}
          />
        )}
      </FocusedPageLayout>
    </ErrorBannerProvider>
  );
}
